import { Label } from "../../../../components/tailwind/label";
import { ConfigField } from "./ConfigField";
import {
  isSchemaFieldVisible,
  schemaToFields
} from "../../utils/workflowSchemaUtils";
import {
  shouldHideInspectorField,
  resolveInspectorFieldValue,
  getFriendlyFieldLabel,
  usesWorkflowPicker,
  resolveWorkflowVersion,
  applyMediaUploadMeta,
  isMediaUploadField
} from "../../utils/workflowInspectorUx";

/** Saved on the API node root — not inside config. */
export const NODE_LEVEL_SCHEMA_FIELDS = new Set([
  "logic",
  "conditions",
  "onTrue",
  "onFalse",
  "onReceived",
  "onSuccess",
  "onFailure",
  "onCompleted",
  "saveToContext"
]);

export function ConfigSchemaForm({
  schema,
  config,
  onConfigChange,
  catalog,
  selectedAction,
  paletteType,
  workflowType,
  workflows,
  workflowsLoading,
  currentWorkflowId,
  saveSignal,
  uploadSilent,
  nodeId,
  onPendingMediaChange,
  onRegisterPendingUploadNode,
  showErrors
}) {
  const fields = schemaToFields(schema);

  return fields.map((field) => {
    if (NODE_LEVEL_SCHEMA_FIELDS.has(field.name)) {
      return null;
    }

    if (shouldHideInspectorField(field.name, paletteType)) {
      return null;
    }
    if (!isSchemaFieldVisible(field, config)) {
      return null;
    }
    if (field.name === "retryPolicy" && !selectedAction?.retrySupported) {
      return null;
    }

    const value = resolveInspectorFieldValue(field, config[field.name]);
    const friendlyHint =
      field.name === "storeAs"
        ? "We use this internally to refer to the answer later in the flow."
        : field.hint;

    return (
      <div className="space-y-2" key={field.name}>
        {!isMediaUploadField(field.name, config, undefined, paletteType) && (
          <>
            <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
              {getFriendlyFieldLabel(field, paletteType)}
            </Label>
            {friendlyHint && <p className="text-xs text-gray-400">{friendlyHint}</p>}
          </>
        )}
        <ConfigField
          field={field}
          value={value}
          onChange={(nextValue, meta) => {
            if (field.name === "action") {
              onConfigChange({ ...config, action: nextValue, input: {} });
              return;
            }
            if (field.name === "type") {
              onConfigChange({ ...config, type: nextValue, message: {} });
              return;
            }
            if (field.name === "workflowId" && usesWorkflowPicker(paletteType)) {
              const target = workflows.find((workflow) => workflow.workflowId === nextValue);
              onConfigChange({
                ...config,
                workflowId: nextValue,
                workflowVersion: target ? resolveWorkflowVersion(target) : config.workflowVersion
              });
              return;
            }
            let nextConfig = { ...config, [field.name]: nextValue };
            nextConfig = applyMediaUploadMeta(nextConfig, field.name, meta);
            onConfigChange(nextConfig);
          }}
          config={config}
          catalog={catalog}
          paletteType={paletteType}
          workflows={workflows}
          workflowsLoading={workflowsLoading}
          currentWorkflowId={currentWorkflowId}
          mediaFilename={config.filename}
          mediaPendingFile={config.pendingFile}
          saveSignal={saveSignal}
          uploadSilent={uploadSilent}
          nodeId={nodeId}
          onPendingMediaChange={onPendingMediaChange}
          onRegisterPendingUploadNode={onRegisterPendingUploadNode}
          showErrors={showErrors}
        />
      </div>
    );
  });
}
